import React, { useState } from 'react';
import { useAuth } from '@/context/Auth';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import AnimatedLogo from '@/components/ui/AnimatedLogo';
import { Separator } from '@/components/ui/separator';
import GoogleIcon from '@/components/ui/GoogleIcon';
import { useToast } from '@/hooks/useToast';
import { Label } from '@/components/ui/label';
import { useTranslation } from 'react-i18next';
import LanguageSelector from '@/components/ui/language-selector';
import { AUTH_ROUTES, getAuthRedirectUrl, mapAuthError } from '@/utils/auth';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLogin, setIsLogin] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isGoogleSubmitting, setIsGoogleSubmitting] = useState(false);
  const { t } = useTranslation();

  const { signUp, signIn, signInWithGoogle } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting || isGoogleSubmitting) return;

    // Check if not logging in, then confirm password
    if (!isLogin) {
      if (password !== confirmPassword) {
        toast({
          title: t('login.toast.error.title.translation'),
          description: t('login.toast.error.passwords_mismatch.translation'),
        });
        return;
      }

      setIsSubmitting(true);
      const { error } = await signUp({
        email,
        password,
        options: {
          emailRedirectTo: getAuthRedirectUrl(AUTH_ROUTES.login),
        },
      });
      setIsSubmitting(false);

      if (error) {
        const mappedError = mapAuthError(error, 'signup');

        toast({
          title: t('login.toast.error.title.translation'),
          description: t(mappedError.translationKey, mappedError.fallback),
        });
        return;
      } else {
        navigate(
          `${AUTH_ROUTES.verifyEmail}?email=${encodeURIComponent(email)}`
        );
        setPassword('');
        setConfirmPassword('');
      }
    } else {
      setIsSubmitting(true);
      const { error } = await signIn({ email, password });
      setIsSubmitting(false);

      if (error) {
        const mappedError = mapAuthError(error, 'signin');

        toast({
          title: t('login.toast.error.title.translation'),
          description: t(mappedError.translationKey, mappedError.fallback),
        });
      } else {
        navigate('/');
      }
    }
  };

  const handleLoginState = () => {
    if (isSubmitting || isGoogleSubmitting) return;
    setIsLogin(!isLogin);
  };

  return (
    <div className="min-h-screen-dynamic flex items-center justify-center p-4 relative bg-background">
      <div className="absolute right-4 top-4">
        <LanguageSelector />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl w-full">
        <div className="flex flex-row space-x-6 justify-center items-center">
          <AnimatedLogo size={90} />
          <Label className="text-5xl md:text-8xl text-foreground">dialup</Label>
        </div>
        <div className="flex flex-col w-full items-center justify-center md:items-start p-2">
          <div className="flex flex-col justify-center md:justify-start p-10">
            <h2 className="text-2xl font-bold py-5 text-foreground">
              {isLogin
                ? t('login.title.translation')
                : t('login.signup.translation')}
            </h2>
            <Button
              type="button"
              variant="outline"
              onClick={async () => {
                if (isSubmitting || isGoogleSubmitting) return;
                try {
                  setIsGoogleSubmitting(true);
                  await signInWithGoogle({
                    options: { redirectTo: window.location.origin },
                  });
                } catch (error) {
                  const mappedError = mapAuthError(
                    error as Error & { code?: string },
                    'signin'
                  );
                  toast({
                    title: t('login.toast.error.title.translation'),
                    description: t(
                      mappedError.translationKey,
                      mappedError.fallback
                    ),
                  });
                } finally {
                  setIsGoogleSubmitting(false);
                }
              }}
              className="bg-card text-foreground gap-3"
              disabled={isSubmitting || isGoogleSubmitting}
            >
              <GoogleIcon />
              {isGoogleSubmitting
                ? t('login.google_signin_loading.translation', 'Redirecting...')
                : t('login.google_signin.translation')}
            </Button>
            <div className="grid grid-cols-3 space-y-2 items-center py-3">
              <Separator />
              <div className="flex flex-row justify-center">
                <p className="text-sm text-muted-foreground">
                  {t('login.or.translation')}
                </p>
              </div>
              <Separator />
            </div>
            <form
              onSubmit={handleSubmit}
              className="flex flex-col space-y-4 full-w"
            >
              <Input
                id="email"
                placeholder={t('login.email.placeholder.translation')}
                value={email}
                onChange={e => setEmail(e.target.value)}
                className="!bg-card !text-foreground !placeholder:text-muted-foreground"
                required
                disabled={isSubmitting || isGoogleSubmitting}
              />
              <div className="flex flex-col items-center gap-4">
                <Input
                  id="password"
                  placeholder={t('login.password.placeholder.translation')}
                  type="password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  className="!bg-card !text-foreground !placeholder:text-muted-foreground"
                  required
                  disabled={isSubmitting || isGoogleSubmitting}
                />
                {isLogin ? (
                  <Button
                    type="button"
                    variant="link"
                    className="!text-foreground hover:!text-foreground/80"
                    onClick={() => navigate(AUTH_ROUTES.forgotPassword)}
                    disabled={isSubmitting || isGoogleSubmitting}
                  >
                    {t('login.forgot_password.translation')}
                  </Button>
                ) : (
                  <Input
                    id="confirm-password"
                    placeholder={t(
                      'login.confirm_password.placeholder.translation'
                    )}
                    type="password"
                    value={confirmPassword}
                    onChange={e => setConfirmPassword(e.target.value)}
                    className="!bg-card !text-foreground !placeholder:text-muted-foreground"
                    required
                    disabled={isSubmitting || isGoogleSubmitting}
                  />
                )}
              </div>

              <Button
                type="submit"
                variant="gradient"
                className="justify-center"
                disabled={isSubmitting || isGoogleSubmitting}
              >
                {isSubmitting
                  ? t('login.submit.loading.translation', 'Working...')
                  : isLogin
                    ? t('login.submit.login.translation')
                    : t('login.submit.signup.translation')}
              </Button>
            </form>

            <Button
              type="button"
              variant="link"
              className="mt-4 !text-foreground hover:!text-foreground/80"
              onClick={handleLoginState}
              disabled={isSubmitting || isGoogleSubmitting}
            >
              {isLogin
                ? t('login.switch.to_signup.translation')
                : t('login.switch.to_login.translation')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
